import React, { useRef, useCallback, useEffect } from 'react'
import { IconButton, Typography } from '@mui/material'
import { Box, Paper } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import RemoveIcon from '@mui/icons-material/Remove'
import { Colors } from '../../../styles/theme'

export default function IncDec({ qty, onIncrItemQty, onDecrItemQty }) {
    const intervalRef = useRef(null)
    const timeoutRef = useRef(null)

    // Tracks the timestamp of the most recent touch event. Used to detect
    // synthesized mouse events (mobile browsers fire mousedown/mouseup
    // ~0–500ms after a tap) and ignore them so a single tap doesn't fire
    // BOTH the touch handler AND the mouse handler — which is what caused
    // the "increments in 2's on mobile" bug.
    const lastTouchAtRef = useRef(0)

    // Centralised timer cleanup
    const clearTimers = useCallback(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current)
            timeoutRef.current = null
        }
        if (intervalRef.current) {
            clearInterval(intervalRef.current)
            intervalRef.current = null
        }
    }, [])

    // Clear any pending/running timers when this component unmounts.
    // Prevents the runaway-interval bug when a CartItem unmounts during
    // a press (e.g. quantity-1 decrement filters the item out of cart
    // before mouseup/touchend can fire).
    useEffect(() => {
        return () => clearTimers()
    }, [clearTimers])

    // Returns true if a touch event happened recently enough that we
    // should treat any incoming mouse event as a synthesized duplicate
    // and ignore it. 500ms is the conventional window — long enough to
    // catch Android Chrome's ~300ms synthesis delay with margin.
    const isSyntheticMouseEvent = useCallback(() => {
        return Date.now() - lastTouchAtRef.current < 500
    }, [])

    // ─── PRESS HANDLERS (modality-agnostic core) ────────────────────────
    const startIncrement = useCallback(() => {
        onIncrItemQty()
        timeoutRef.current = setTimeout(() => {
            intervalRef.current = setInterval(() => {
                onIncrItemQty()
            }, 100)
        }, 400)
    }, [onIncrItemQty])

    const startDecrement = useCallback(() => {
        onDecrItemQty()
        timeoutRef.current = setTimeout(() => {
            intervalRef.current = setInterval(() => {
                onDecrItemQty()
            }, 100)
        }, 400)
    }, [onDecrItemQty])

    // ─── TOUCH HANDLERS ─────────────────────────────────────────────────
    const handleIncrementTouchStart = useCallback(() => {
        lastTouchAtRef.current = Date.now()
        startIncrement()
    }, [startIncrement])

    const handleDecrementTouchStart = useCallback(() => {
        lastTouchAtRef.current = Date.now()
        startDecrement()
    }, [startDecrement])

    const handleTouchEnd = useCallback(
        (e) => {
            lastTouchAtRef.current = Date.now()
            // Suppress the synthesized mouse events that mobile browsers
            // fire after a tap. This is the primary fix for the
            // "increments in 2's" bug.
            if (e.cancelable) e.preventDefault()
            clearTimers()
        },
        [clearTimers],
    )

    // ─── MOUSE HANDLERS (with synthetic-event guard) ────────────────────
    const handleIncrementMouseDown = useCallback(() => {
        if (isSyntheticMouseEvent()) return
        startIncrement()
    }, [isSyntheticMouseEvent, startIncrement])

    const handleDecrementMouseDown = useCallback(() => {
        if (isSyntheticMouseEvent()) return
        startDecrement()
    }, [isSyntheticMouseEvent, startDecrement])

    const handleMouseUp = useCallback(() => {
        clearTimers()
    }, [clearTimers])

    const handleMouseLeave = useCallback(() => {
        clearTimers()
    }, [clearTimers])

    return (
        <Paper
            elevation={18}
            sx={{
                width: 'fit-content',
                display: 'inline-flex',
            }}
        >
            <Box display="flex" sx={{ mt: 0 }}>
                <IconButton
                    sx={{
                        borderRadius: 1.0,
                        border: `2px solid ${Colors.companyColors.eb_bolt}`,
                        color: Colors.white,
                        background: `${Colors.secondary}`,
                        minWidth: '44px',
                        minHeight: '44px',
                        mr: '-1.8px',
                        px: 0.8,
                        py: 0.4,
                        // Disable browser tap-to-click synthesis fallback.
                        // Some mobile browsers also fire a 'click' event
                        // ~300ms after touchend; this CSS hint helps them
                        // skip that delay and reduces double-firing risk.
                        touchAction: 'manipulation',
                        '&:hover': {
                            backgroundColor: `${Colors.companyColors.greenLight}`,
                        },
                    }}
                    onMouseDown={handleDecrementMouseDown}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseLeave}
                    onTouchStart={handleDecrementTouchStart}
                    onTouchEnd={handleTouchEnd}
                    onTouchCancel={handleTouchEnd}
                >
                    <RemoveIcon />
                </IconButton>

                <Box
                    sx={{
                        border: `2px solid ${Colors.companyColors.eb_bolt}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: '40px',
                        minHeight: '36px',
                        px: 1,
                    }}
                >
                    <Typography
                        variant="h6"
                        sx={{
                            lineHeight: 1,
                            margin: 0,
                            padding: 0,
                        }}
                    >
                        {qty}
                    </Typography>
                </Box>

                <IconButton
                    sx={{
                        borderRadius: 1.0,
                        border: `2px solid ${Colors.companyColors.eb_bolt}`,
                        color: Colors.white,
                        background: `${Colors.secondary}`,
                        ml: '-1.8px',
                        py: 0.4,
                        touchAction: 'manipulation',
                        '&:hover': {
                            backgroundColor: `${Colors.companyColors.greenLight}`,
                        },
                    }}
                    onMouseDown={handleIncrementMouseDown}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseLeave}
                    onTouchStart={handleIncrementTouchStart}
                    onTouchEnd={handleTouchEnd}
                    onTouchCancel={handleTouchEnd}
                >
                    <AddIcon />
                </IconButton>
            </Box>
        </Paper>
    )
}
